import React from 'react';
import { FileText, Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Header } from './Header';
import { Footer } from './Footer';
import { usePageMeta } from '../hooks/usePageMeta';

const Section = ({ id, title, children }) => (
  <section id={id} data-testid={`terms-section-${id}`} className="mb-10">
    <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 leading-tight">
      {title}
    </h2>
    <div className="space-y-4 text-gray-700 text-base leading-relaxed">
      {children}
    </div>
  </section>
);

export const TermsOfService = () => {
  usePageMeta({
    title: 'Terms of Service | Tower Technology',
    description:
      'Read the Terms of Service governing use of the Tower Technology website and information relating to engineering and technical support services in China.',
    canonical: 'https://towertech-eng.com/terms-of-service',
  });
  return (
    <>
      <Header />
      <main className="pt-32 pb-24 bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50 min-h-screen">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-700 rounded-full mb-6">
                <FileText className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                Terms of Service
              </h1>
              <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-blue-800 mb-6"></div>
              <p className="text-base md:text-lg text-gray-600">
                Effective date: September 2, 2026
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-7 md:p-10">
              <p className="text-gray-700 leading-relaxed mb-8">
                These Terms of Service ("Terms") govern the use of the website{' '}
                <a
                  href="https://towertech-eng.com"
                  className="text-blue-700 hover:text-blue-800 underline"
                >
                  towertech-eng.com
                </a>{' '}
                operated by Xiamen Tower Technology Co., Ltd. ("Tower
                Technology"). By accessing or using this website you agree to
                these Terms.
              </p>

              <Section id="general-information" title="1. General Information">
                <p>
                  The information published on this website is provided for
                  general business information about Tower Technology and the
                  types of engineering and technical support services we may
                  provide.
                </p>
                <p>
                  Website content may be updated, modified or removed at any
                  time without prior notice.
                </p>
              </Section>

              <Section id="no-binding-offer" title="2. No Binding Offer">
                <p>
                  Descriptions of services, personnel categories, industries
                  supported or capabilities presented on this website do not
                  automatically constitute a binding offer or contract.
                </p>
                <p>
                  Specific engineering and technical support services,
                  personnel availability, scope of work, pricing, schedules,
                  responsibilities and applicable commercial terms are agreed
                  separately in written quotations, proposals or contracts
                  between Tower Technology and the customer.
                </p>
              </Section>

              <Section id="personnel-availability" title="3. Personnel Availability">
                <p>
                  Availability of engineers, supervisors and technical
                  specialists is subject to project requirements, scheduling
                  and mutual agreement. Nothing on this website should be
                  interpreted as a guarantee that specific personnel will be
                  available for a particular project or period.
                </p>
              </Section>

              <Section id="project-responsibility" title="4. Project Responsibility">
                <p>
                  Tower Technology is not automatically responsible for the
                  complete execution of a customer's industrial project.
                  Responsibilities of Tower Technology and of the customer for
                  each project are defined in the applicable agreement or
                  contract for that project.
                </p>
                <p>
                  Where Tower Technology provides personnel to support the
                  customer's project organization, the scope of that support
                  and any related responsibilities are set out in the relevant
                  contract.
                </p>
              </Section>

              <Section id="use-of-website" title="5. Use of the Website">
                <p>Users must not:</p>
                <ul className="list-disc pl-6 space-y-1.5">
                  <li>Misuse the website or attempt to disrupt its operation</li>
                  <li>Submit false, misleading or fraudulent information through the contact or project inquiry form</li>
                  <li>Use the website or contact form to send unsolicited advertising, spam or unlawful content</li>
                  <li>Attempt to gain unauthorized access to the website or any related systems</li>
                </ul>
              </Section>

              <Section id="intellectual-property" title="6. Intellectual Property">
                <p>
                  Unless otherwise stated, all content published on this
                  website — including text, layout, graphics, logos and the
                  Tower Technology name — belongs to Tower Technology and is
                  protected by applicable intellectual property laws. Content
                  may not be copied, reproduced or redistributed without prior
                  written permission, except for reasonable personal or
                  internal business reference use.
                </p>
              </Section>

              <Section id="third-party-links" title="7. Third-Party Links">
                <p>
                  This website may contain links to third-party websites for
                  reference purposes. Tower Technology is not responsible for
                  the content or practices of third-party websites.
                </p>
              </Section>

              <Section id="applicable-commercial-terms" title="8. Applicable Commercial Terms">
                <p>
                  Commercial terms applicable to actual engineering and
                  technical support projects — including scope, deliverables,
                  responsibilities, schedule, pricing, payment terms, warranty,
                  liability and governing law — are defined separately in the
                  quotation, order confirmation or contract for the relevant
                  project.
                </p>
                <p>
                  Nothing on this website replaces or overrides the terms of
                  such written agreements.
                </p>
              </Section>

              <Section id="changes" title="9. Changes to These Terms">
                <p>
                  Tower Technology may update these Terms from time to time.
                  The current version is always available on this page.
                  Continued use of the website following any update
                  constitutes acceptance of the revised Terms.
                </p>
              </Section>

              {/* Important Notice */}
              <div className="mt-4 bg-blue-50 border-l-4 border-blue-700 rounded-lg p-5 md:p-6">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-6 h-6 text-blue-700 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 mb-2">
                      Important Notice
                    </h3>
                    <p className="text-gray-700 leading-relaxed">
                      These Terms provide a general framework for the use of
                      the website. Any specific engineering, personnel or
                      project-related commitments between Tower Technology and
                      a customer are governed exclusively by the applicable
                      written contract for that project.
                    </p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-xl p-6 md:p-8 text-white mt-8">
                <div className="flex items-start space-x-4">
                  <div className="inline-flex items-center justify-center w-11 h-11 bg-white/20 rounded-lg flex-shrink-0">
                    <Mail className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl md:text-2xl font-bold mb-2">
                      10. Contact
                    </h2>
                    <p className="text-blue-100 mb-4 leading-relaxed">
                      For any questions about these Terms, please contact:
                    </p>
                    <a
                      href="mailto:office@towertech-eng.com"
                      className="inline-flex items-center text-white font-semibold hover:text-blue-100 transition-colors break-all"
                    >
                      <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                      office@towertech-eng.com
                    </a>
                    <div className="mt-4 text-sm text-blue-100 leading-relaxed">
                      <div className="font-semibold text-white">
                        Xiamen Tower Technology Co., Ltd.
                      </div>
                      <div>Xiamen (Fujian), China</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Link
                to="/"
                data-testid="terms-back-home"
                className="inline-flex items-center px-6 py-3 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
};
